import { createApi, retry } from "@reduxjs/toolkit/query/react";
import { createFlatBufferBaseQuery } from "../../flatbuffers/api/flatBufferBaseQuery";

const maxRetries = 2;

const baseQuery = retry(
  createFlatBufferBaseQuery(process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? ""),
  { maxRetries },
);

export const adapterAPI = createApi({
  reducerPath: "adapterAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: ["tagGetAdapters", "tagGetAdapter"],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    //* *********************************************************** */
    //* GET ADAPTER */
    getAdapter: builder.query({
      query: (body: any) => ({
        url: `/adapters/${body.id}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetAdapter"],
    }),
    //* *********************************************************** */
    //* GET ALL ADAPTERS */
    getAdapters: builder.query({
      query: (body: any) => ({
        url: `/adapters/?sort=created_at&sort_direction=desc&page=${body.page}&limit=${body.limit}${
          body.searchAndFilterString || ""
        }`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetAdapters"],
    }),
    //* *********************************************************** */
    //* GET ALL ADAPTERS SUMMARY */
    getAdaptersSummary: builder.query({
      query: (body: any) => ({
        url: `/adapters/summary/?sort=name&sort_direction=asc&page=${body.page}&limit=${
          body.limit
        }${body.searchAndFilterString || ""}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetAdapters"],
    }),
    //* *********************************************************** */
    //* CREATE ADAPTER */
    postAdapter: builder.mutation({
      query: (body: any) => ({
        url: `/adapters/`,
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "CreateAdapter" },
      invalidatesTags: ["tagGetAdapters"],
    }),
    //* *********************************************************** */
    //* DELETE ADAPTERS */
    deleteAdapter: builder.mutation({
      query: (body: any) => ({
        url: `/adapters/${body.id}`,
        method: "DELETE",
        headers: { authorization: body.token },
        responseHandler: "text",
      }),
      invalidatesTags: ["tagGetAdapters"],
    }),
    //* *********************************************************** */
    //* EDIT ADAPTER */
    putAdapter: builder.mutation({
      query: (body: any) => ({
        url: `/adapters/${body.id}`,
        method: "PUT",
        body: { ...body.body, id: body.id },
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "UpdateAdapter" },
      invalidatesTags: ["tagGetAdapters", "tagGetAdapter"],
    }),
    //* *********************************************************** */
    //* ASSIGN ACTION */
    putAssignAction: builder.mutation({
      query: (body: any) => ({
        url: `/adapters/actions/${body.id}`,
        method: "PUT",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetAdapters"],
    }),
  }),
});

export const {
  useGetAdapterQuery,
  useGetAdaptersSummaryQuery,
  useLazyGetAdapterQuery,
  useLazyGetAdaptersQuery,
  useLazyGetAdaptersSummaryQuery,
  usePostAdapterMutation,
  useDeleteAdapterMutation,
  usePutAdapterMutation,
  usePutAssignActionMutation,
} = adapterAPI;
